import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { changeFormField } from 'apps/auth/actions/formAction';
import { BRAZE_BANNER_PLACEMENTS } from 'apps/auth/analytics/braze';
import BrazeBannerPlacement from 'apps/auth/components/braze/BrazeBannerPlacement';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import SuggestionModal from 'apps/auth/components/common/Modal/SuggestionModal';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import CompactDeliveryMethod from 'apps/auth/components/common/send/deliveryOptions/CompactDeliveryMethod';
import DisabledCompactDeliveryMethod from 'apps/auth/components/common/send/deliveryOptions/DisabledCompactDeliveryMethod';
import GiftBlock from 'apps/auth/components/common/send/deliveryOptions/GiftBlock/GiftBlock';
import FeeTextPopup from 'apps/auth/components/common/send/popups/FeeTextPopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import PostSelectCountryDisclaimer from 'apps/auth/components/send/disclaimers/PostSelectCountryDisclaimer';
import PayoutStoriesModal from 'apps/auth/components/send/modals/PayoutStoriesModal';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import { getRecipientTypeParam } from 'apps/auth/components/utils/AmplitudeUtils';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { DELIVERY_METHOD_LIST_SHOWN, FEE_INFO_CLICKED, REQUEST_DELIVERY_OPTION_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
    PAYOUT_METHOD_CATEGORY,
    RECIPIENT_TYPE,
    SCREEN_TYPE,
    SCREEN,
    PAYMENT_COUNTRY,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { DELIVERY_METHOD_LIST, NEW } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { BRAZE_IAM_EVENT_PROPS, BRAZE_IAM_PLACEMENTS, BRAZE_IAM_SCREEN_EVENTS } from 'apps/auth/constants/braze/brazeIam';
import { DELIVERY_CONTACT } from 'apps/auth/constants/Constants';
import { ACCOUNT_TO, TO_PHONE_NUMBER } from 'apps/auth/constants/fieldsName';
import { GA_SEND_FORM_NAME, SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_BONUS_WITHDRAW, AUTH_GA_POCKET, AUTH_SEND_LINK } from 'apps/auth/constants/UrlConstants';
import update from 'apps/auth/helpers/Immutability';
import { useAuth, useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';
import { useBrazeIamScreen } from 'apps/auth/hooks/useBrazeIamScreen';
import { useQuery } from 'apps/auth/hooks/useQuery';
import { showPromoModal } from 'apps/auth/services/promoModalService';

function useCheckCountryForBonusWithdraw(props, isBonus, noMethods, messages, popupRef) {
    const navigate = useNavigate();

    useEffect(() => {
        if (isBonus && noMethods) {
            popupRef.current?.open(
                <TextPopup
                    text={messages.countryUnavailableForBonusWithdraw}
                    buttonText={messages.gotIt}
                    onClick={() => {
                        popupRef.current?.close();
                        navigate(AUTH_BONUS_WITHDRAW);
                    }}
                    popupRef={popupRef}
                />,
            );
        }
    }, [noMethods]);
}

const DeliveryOptionsPage = ({ stateOptions: { isGA, isBonus }, ...props }) => {
    const { ballId } = useAuth();
    const {
        postCountrySelectDisclaimer,
        feedbackFormAvailable,
        authPromoAction,
        noMethods,
        pocket,
        toCountry,
        sendToContact,
        toContactCommission,
        deliveryMethodsOrder = {},
        availableDeliveryMethodsList = {},
        sendFlowV3,
        toCurrency,
        userCountry,
        promotionSliders,
    } = useExtra();
    const query = useQuery();

    const messages = usePageMessages();
    const popupRef = useRef(null);
    const popupFullwidthRef = useRef(null);
    const modalRef = useRef(null);
    const form = useForm(SEND_FORM_NAME);
    const accountToField = form?.fields?.[ACCOUNT_TO];
    const accountToPhoneNumber = form?.fields?.[TO_PHONE_NUMBER];
    const toCountryId = toCountry?.id;
    const hasDeliveryMethodsList = Object.keys(availableDeliveryMethodsList).length > 0;
    const hasDeliveryMethodsOrder = deliveryMethodsOrder?.sourceOptionsGroups?.length > 0;
    const recipientType = query.get('type');
    const sendUriPostfix = recipientType ? `?type=${recipientType}` : '';

    useBrazeIamScreen(
        BRAZE_IAM_SCREEN_EVENTS.DELIVERY_OPTIONS,
        BRAZE_IAM_PLACEMENTS.DELIVERY_OPTIONS,
        {
            [BRAZE_IAM_EVENT_PROPS.COUNTRY_TO]: toCountry?.code?.toUpperCase(),
        },
        { ready: Boolean(toCountry?.code) },
    );

    useEffect(() => {
        if (hasDeliveryMethodsList && hasDeliveryMethodsOrder) {
            amplitude.getInstance().logEvent(DELIVERY_METHOD_LIST_SHOWN, {
                [PAYOUT_COUNTRY]: toCountry?.nameEn,
                [SCREEN_TYPE]: NEW,
                [RECIPIENT_TYPE]: getRecipientTypeParam(recipientType),
            });
        }
    }, [hasDeliveryMethodsList, hasDeliveryMethodsOrder]);

    useEffect(() => {
        const showModal = showPromoModal(ballId, promotionSliders, 'payout_promo_info', true);
        if (showModal) {
            popupFullwidthRef.current?.open(<PayoutStoriesModal sliders={promotionSliders} popupFullwidthRef={popupFullwidthRef} />);
        }
    }, [promotionSliders]);

    const initBackUrl = () => {
        if (isBonus) {
            return AUTH_BONUS_WITHDRAW + sendUriPostfix;
        }

        return (isGA ? `${AUTH_GA_POCKET}${pocket?.id}/send` : AUTH_SEND_LINK) + sendUriPostfix;
    };

    const backUrl = initBackUrl();
    useEffect(() => {
        if (postCountrySelectDisclaimer) {
            props.toggleModal(true, <PostSelectCountryDisclaimer disclaimer={postCountrySelectDisclaimer} toCountryId={toCountryId} />, false);
        }
    }, [postCountrySelectDisclaimer]);

    const clearValue = (oldField) => {
        const formName = isGA ? GA_SEND_FORM_NAME : SEND_FORM_NAME;
        const newFieldCode = update(oldField, {
            value: { $set: null },
        });
        props.changeFormField(formName, newFieldCode);
    };

    useEffect(() => {
        const propsArr = [accountToField, accountToPhoneNumber];
        propsArr.forEach((currProp) => currProp && clearValue(currProp));
    }, []);

    const openSuggestionPopup = (e) => {
        e?.preventDefault();
        props.toggleModal(true, <SuggestionModal />);
    };

    useCheckCountryForBonusWithdraw(
        props,
        isBonus,
        noMethods,
        {
            countryUnavailableForBonusWithdraw: messages.countryUnavailableForBonusWithdraw,
            gotIt: messages.gotIt,
        },
        popupRef,
    );

    const getMethods = () => {
        if (hasDeliveryMethodsList && hasDeliveryMethodsOrder) {
            const filterArrGroups = Object.values(deliveryMethodsOrder?.sourceOptionsGroups).filter(({ paymentOptionsItems }) => {
                return paymentOptionsItems.some(({ category }) => {
                    return availableDeliveryMethodsList.hasOwnProperty(category);
                });
            });
            const sortGroupsArr = filterArrGroups.sort((a, b) => a.orderId - b.orderId);

            return sortGroupsArr.map(({ groupId, technicalDescription, description, paymentOptionsItems, feeInfo, feeInfoAdditional }, index) => {
                const sortedMethodsArr = paymentOptionsItems.sort((a, b) => a.order - b.order);
                return (
                    <div key={`${groupId}-${index}`} data-testid="delivery-methods-sorted">
                        {sendFlowV3 ? (
                            <Separator
                                isSubheader
                                marginTop="16px"
                                marginBottom="16px"
                                leftText={description}
                                rightText={feeInfo}
                                hasRightTextInfo={!!feeInfoAdditional}
                                onClick={() => {
                                    amplitude.getInstance().logEvent(FEE_INFO_CLICKED, {
                                        [SCREEN]: DELIVERY_METHOD_LIST,
                                        [PAYOUT_METHOD_CATEGORY]: technicalDescription,
                                        [PAYOUT_CURRENCY]: toCurrency?.code,
                                        [PAYOUT_COUNTRY]: toCountry?.nameEn,
                                        [PAYMENT_COUNTRY]: userCountry?.nameEn,
                                    });
                                    popupRef.current?.open(<FeeTextPopup text={feeInfoAdditional} isShowTerms={false} popupRef={popupRef} />);
                                }}
                            />
                        ) : (
                            <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                                {description}
                            </Text>
                        )}
                        {sortedMethodsArr.map(({ category, badge, actionBanner }, i) => {
                            const deliveryMethod = availableDeliveryMethodsList[category];
                            if (deliveryMethod) {
                                return deliveryMethod?.source?.temporarilyUnavailableSource ? (
                                    <DisabledCompactDeliveryMethod
                                        key={`${category}${i}`}
                                        deliveryMethod={deliveryMethod.method}
                                        countrySource={deliveryMethod.source}
                                        countryToId={toCountryId}
                                        badge={badge}
                                        actionBanner={actionBanner}
                                        isGA={isGA}
                                        temporarilyUnavailableReason={deliveryMethod?.source?.temporarilyUnavailableReason}
                                        isBonus={isBonus}
                                    />
                                ) : (
                                    <CompactDeliveryMethod
                                        key={`${category}${i}`}
                                        deliveryMethod={deliveryMethod.method}
                                        countrySource={deliveryMethod.source}
                                        commission={deliveryMethod.commission}
                                        countryToId={toCountryId}
                                        badge={badge}
                                        actionBanner={actionBanner}
                                        isGA={isGA}
                                        messages={messages}
                                        isBonus={isBonus}
                                        payoutMethodCategory={technicalDescription}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                );
            });
        }
    };

    return (
        <>
            <CommonPage backUrl={backUrl} stateOptions={props.stateOptions}>
                <HeaderPanel title={messages.title} indentSmall={sendFlowV3} />
                {authPromoAction && <GiftBlock promoAction={authPromoAction} countryToId={toCountryId} popupRef={popupRef} />}
                {sendToContact && (
                    <ul>
                        <CompactDeliveryMethod
                            key={`${DELIVERY_CONTACT}_method`}
                            deliveryMethod={DELIVERY_CONTACT}
                            countryToId={toCountryId}
                            countrySource={sendToContact}
                            commission={toContactCommission}
                            isGA={isGA}
                            isBonus={isBonus}
                        />
                    </ul>
                )}
                <ul>{getMethods()}</ul>
                <BrazeBannerPlacement placementId={BRAZE_BANNER_PLACEMENTS.DELIVERY_OPTIONS} recipientCountry={toCountry?.code} marginTop="24px" />
                {feedbackFormAvailable?.delivery === 'true' && (
                    <Button
                        type="button"
                        size="tight"
                        variant="row"
                        caption={messages.suggestedButton}
                        onClick={() => {
                            amplitude.getInstance().logEvent(REQUEST_DELIVERY_OPTION_CLICKED);
                            openSuggestionPopup();
                        }}
                        negativeMargin
                    />
                )}
            </CommonPage>
            <PopupNew ref={popupRef} />
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </>
    );
};
export default connect(null, { toggleModal, changeFormField })(BasePage(DeliveryOptionsPage));
